// The builder's live data: the core's report on the document (debounced, newest answer wins),
// saving as the setter works, and waiting on worker jobs.
import type {
  BuildJobResponse,
  BuildJobStatus,
  BuildReport,
  DraftFogReport,
  PuzzleDoc,
  SaveDraftRequest,
} from '@zinc/shared';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { core, coreAvailable } from '../core/client';
import type { Work } from './model';

export interface ReportState {
  report: BuildReport | null;
  /** The core couldn't read the document (a half-drawn region, say). */
  error: string | null;
  busy: boolean;
}

/** The core's report on the document, a moment after each change. */
export function useBuildReport(doc: PuzzleDoc, candidates: boolean, delayMs = 200): ReportState {
  const available = coreAvailable();
  const [answer, setAnswer] = useState<{
    report: BuildReport | null;
    error: string | null;
    /** The document and setting the answer is for: anything else is still being worked on. */
    for: { doc: PuzzleDoc; candidates: boolean } | null;
  }>({ report: null, error: null, for: null });
  const latest = useRef(0);
  useEffect(() => {
    if (!available) return;
    const id = ++latest.current;
    const timer = setTimeout(() => {
      core
        .builderReport(doc, candidates)
        .then((report) => {
          if (id === latest.current) setAnswer({ report, error: null, for: { doc, candidates } });
        })
        .catch((err: unknown) => {
          if (id === latest.current)
            setAnswer((a) => ({
              report: a.report,
              error: err instanceof Error ? err.message : String(err),
              for: { doc, candidates },
            }));
        });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [doc, candidates, delayMs, available]);
  if (!available) return { report: null, error: 'The solver needs Web Workers.', busy: false };
  const busy = answer.for?.doc !== doc || answer.for.candidates !== candidates;
  return { report: answer.report, error: answer.error, busy };
}

export interface FogState {
  fog: DraftFogReport | null;
  error: string | null;
  busy: boolean;
}

/** How the draft solves through its fog, a moment after each change (none without fog). */
export function useFogReport(doc: PuzzleDoc, delayMs = 600): FogState {
  const available = coreAvailable();
  const [answer, setAnswer] = useState<{
    fog: DraftFogReport | null;
    error: string | null;
    for: PuzzleDoc | null;
  }>({ fog: null, error: null, for: null });
  const latest = useRef(0);
  const on = Boolean(doc.fog);
  useEffect(() => {
    if (!available || !on) return;
    const id = ++latest.current;
    const timer = setTimeout(() => {
      core
        .fog({ doc })
        .then((fog) => {
          if (id === latest.current) setAnswer({ fog, error: null, for: doc });
        })
        .catch((err: unknown) => {
          if (id === latest.current)
            setAnswer({
              fog: null,
              error: err instanceof Error ? err.message : String(err),
              for: doc,
            });
        });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [doc, on, delayMs, available]);
  if (!on) return { fog: null, error: null, busy: false };
  return { fog: answer.fog, error: answer.error, busy: answer.for !== doc };
}

export interface SaveState {
  saving: boolean;
  error: string | null;
  /** Saves now (before Test solve or Mark ready). */
  flush: () => Promise<void>;
}

/** Saves the draft a little after the last change. */
export function useAutosave(id: string, work: Work, enabled: boolean, delayMs = 700): SaveState {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saved = useRef<Work>(work);
  const pending = useRef<Work | null>(null);
  const inFlight = useRef<Promise<void>>(Promise.resolve());

  const save = useCallback(async () => {
    const w = pending.current;
    if (!w) return;
    pending.current = null;
    setSaving(true);
    const body: SaveDraftRequest = { title: w.title, document: w.doc, builder: w.builder };
    try {
      await api(`/api/builds/${id}`, { method: 'PUT', json: body });
      saved.current = w;
      setError(null);
    } catch (err) {
      pending.current ??= w;
      setError((err as Error).message || 'Not saved.');
    } finally {
      setSaving(false);
    }
  }, [id]);

  useEffect(() => {
    if (!enabled || work === saved.current) return;
    pending.current = work;
    const timer = setTimeout(() => {
      inFlight.current = inFlight.current.then(save);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [work, enabled, delayMs, save]);

  const flush = useCallback(async () => {
    if (enabled && work !== saved.current) pending.current = work;
    inFlight.current = inFlight.current.then(save);
    await inFlight.current;
    if (pending.current) throw new Error('The draft could not be saved.');
  }, [work, enabled, save]);

  return { saving, error, flush };
}

/** A worker job the builder started (Mark ready, Complete, Automatic), polled until it ends. */
export function useBuildJob<T>(jobId: string | null) {
  return useQuery({
    queryKey: ['build-job', jobId],
    queryFn: () => api<BuildJobStatus<T>>(`/api/builds/jobs/${jobId}`),
    enabled: jobId !== null,
    refetchInterval: (q) =>
      q.state.data?.state === 'done' || q.state.data?.state === 'failed' ? false : 800,
  });
}

export async function startJob(path: string, json?: unknown): Promise<string> {
  const r = await api<BuildJobResponse>(path, { method: 'POST', json });
  return r.jobId;
}
